import { useEffect, useState } from "react";
import { C, Badge, Button, Skeleton } from "@coremonit/design-system";
import { CheckCircle2, XCircle, RefreshCw, Loader2 } from "lucide-react";
import { StepHeader } from "./common";
import type { WizardData } from "../wizardStore";

const CHECKS = [
  { id: "connectivity", label: "Connectivity",   message: "TCP handshake ok" },
  { id: "metrics",      label: "Metrics ingest", message: "Received 3 sample series" },
  { id: "logs",         label: "Log stream",     message: "Tailed 25 log lines" },
  { id: "traces",       label: "Trace ingest",   message: "OTLP endpoint reachable" },
  { id: "health",       label: "Health probe",   message: "GET /healthz returned 200" },
];

interface Props { data: WizardData; update: (p: Partial<WizardData>) => void }

export function Step6Validation({ data, update }: Props) {
  const [running, setRunning] = useState<string | null>(null);
  const [results, setResults] = useState<WizardData["validationResults"]>(data.validationResults);

  const run = async () => {
    setResults([]);
    const out: NonNullable<WizardData["validationResults"]> = [];
    for (const c of CHECKS) {
      setRunning(c.id);
      const latency = 100 + Math.floor(Math.random() * 250);
      await new Promise((r) => setTimeout(r, latency + 250));
      const step = { id: c.id, label: c.label, status: "passed", latencyMs: latency, message: c.message };
      out.push(step);
      setResults([...out]);
    }
    setRunning(null);
    update({ validationResults: out });
  };

  useEffect(() => {
    if (!data.validationResults) run();
  }, []); // eslint-disable-line

  return (
    <div>
      <StepHeader title="Connection validation" description="Runs a live probe against the service. Each check must pass before continuing." />
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
        {CHECKS.map((c) => {
          const r = results?.find((x) => x.id === c.id);
          const isRunning = running === c.id;
          const status = r ? r.status : isRunning ? "running" : "pending";
          return (
            <div key={c.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "10px 14px", background: C.surfaceHigh, border: `1px solid ${status === "passed" ? "rgba(16,185,129,0.3)" : status === "failed" ? "rgba(239,68,68,0.3)" : C.border}`, borderRadius: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {status === "passed" && <CheckCircle2 size={16} color={C.green} />}
                {status === "failed" && <XCircle size={16} color={C.red} />}
                {status === "running" && <Loader2 size={16} color={C.blue} style={{ animation: "cm-spin 1s linear infinite" }} />}
                {status === "pending" && <div style={{ width: 16, height: 16, borderRadius: "50%", border: `2px solid ${C.textMuted}` }} />}
                <div>
                  <div style={{ color: C.text, fontSize: 12, fontWeight: 600 }}>{c.label}</div>
                  <div style={{ color: C.textMuted, fontSize: 10, marginTop: 2 }}>{r?.message ?? (isRunning ? "probing…" : "queued")}</div>
                </div>
              </div>
              {r && <Badge color={r.status === "passed" ? "green" : "red"}>{r.latencyMs}ms · {r.status}</Badge>}
            </div>
          );
        })}
      </div>
      <Button variant="secondary" icon={<RefreshCw size={12} />} onClick={run} disabled={!!running}>Re-run all checks</Button>
    </div>
  );
}
